import { useEffect, useState } from 'react';

import ReferralQrWithLogo from '../apprenant/ReferralQrWithLogo';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { fetchPublicBranding } from '../../services/api';
import {
  buildPlatformRegisterUrl,
  downloadPlatformQrImage,
  sharePlatformQrImage,
} from '../../services/parrainageService';

type Props = {
  /** `footer` : carte contact accueil · `admin` : rail tableau de bord */
  variant?: 'footer' | 'admin';
  qrSize?: number;
  /** URL d’inscription (API admin). Sinon chargée depuis /settings/branding. */
  registerUrl?: string | null;
};

export default function PublicPlatformQrCard({
  variant = 'footer',
  qrSize,
  registerUrl: registerUrlProp,
}: Props) {
  const [registerUrl, setRegisterUrl] = useState<string | null>(registerUrlProp ?? null);

  useEffect(() => {
    if (registerUrlProp) {
      setRegisterUrl(registerUrlProp);
      return;
    }
    let cancelled = false;
    void fetchPublicBranding()
      .then((b) => {
        if (cancelled) return;
        const url =
          (b as { platformRegisterUrl?: string } | null)?.platformRegisterUrl ??
          buildPlatformRegisterUrl();
        setRegisterUrl(url);
      })
      .catch(() => {
        if (!cancelled) setRegisterUrl(buildPlatformRegisterUrl());
      });
    return () => {
      cancelled = true;
    };
  }, [registerUrlProp]);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const size = qrSize ?? (variant === 'admin' ? 140 : 120);

  if (!registerUrl) {
    return (
      <p className="text-center text-xs text-on-surface-variant">
        {variant === 'footer' ? 'Chargement du QR…' : 'Chargement…'}
      </p>
    );
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(registerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setHint('Copie impossible — sélectionnez le lien manuellement.');
    }
  };

  const shareQr = async () => {
    setBusy(true);
    setHint(null);
    try {
      const result = await sharePlatformQrImage({ registerUrl });
      if (result.mode === 'download') {
        setHint('QR téléchargé — partagez l’image (affiche, WhatsApp, etc.).');
      }
    } catch (e) {
      setHint(e instanceof Error ? e.message : 'Partage impossible.');
    } finally {
      setBusy(false);
    }
  };

  const downloadQr = async () => {
    setBusy(true);
    setHint(null);
    try {
      await downloadPlatformQrImage(registerUrl);
      setHint('Image QR enregistrée.');
    } catch (e) {
      setHint(e instanceof Error ? e.message : 'Téléchargement impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'admin') {
    return (
      <div className="public-platform-qr public-platform-qr--admin">
        <div className="public-platform-qr__admin-head">
          <LucideSymbolIcon name="qr_code" tone="primary" size={20} />
          <div>
            <p className="text-xs font-bold">QR inscription plateforme</p>
            <p className="mt-0.5 text-[10px] text-on-surface-variant">
              Mène à la page « Créer un compte »
            </p>
          </div>
        </div>
        <div className="public-platform-qr__frame public-platform-qr__frame--sm">
          <ReferralQrWithLogo
            registerUrl={registerUrl}
            size={size}
            alt="QR code inscription JCONSULT MY Academia"
          />
        </div>
        <div className="public-platform-qr__actions">
          <button type="button" className="public-platform-qr__btn" disabled={busy} onClick={() => void copyLink()}>
            {copied ? 'Copié' : 'Lien'}
          </button>
          <button type="button" className="public-platform-qr__btn" disabled={busy} onClick={() => void downloadQr()}>
            PNG
          </button>
          <button
            type="button"
            className="public-platform-qr__btn public-platform-qr__btn--primary"
            disabled={busy}
            onClick={() => void shareQr()}
          >
            {busy ? '…' : 'Partager'}
          </button>
        </div>
        {hint ? <p className="public-platform-qr__hint">{hint}</p> : null}
      </div>
    );
  }

  return (
    <div className="public-platform-qr public-platform-qr--footer">
      <div className="public-platform-qr__frame">
        <ReferralQrWithLogo
          registerUrl={registerUrl}
          size={size}
          alt="QR code inscription JCONSULT MY Academia"
        />
      </div>
      <p className="public-platform-qr__caption">Scannez pour créer votre compte</p>
      {hint ? <p className="public-platform-qr__hint">{hint}</p> : null}
      <div className="public-platform-qr__actions public-platform-qr__actions--footer">
        <button
          type="button"
          className="rounded-public-btn border border-white/25 px-2.5 py-1.5 text-[10px] font-bold text-white hover:bg-white/10"
          disabled={busy}
          onClick={() => void copyLink()}
        >
          {copied ? 'Lien copié' : 'Copier le lien'}
        </button>
        <button
          type="button"
          className="rounded-public-btn border border-white/25 px-2.5 py-1.5 text-[10px] font-bold text-white hover:bg-white/10"
          disabled={busy}
          onClick={() => void downloadQr()}
        >
          Télécharger
        </button>
      </div>
    </div>
  );
}
